<script lang="ts" setup>
import { debounce } from 'lodash/fp';
import ResizeObserver from 'resize-observer-polyfill';
import { computed, ref, onBeforeUnmount, watch, onMounted, type PropType, toRefs } from 'vue';

import { orderformService } from '@exchange/libs/order/order-form/service/src';
import { OrderType } from '@exchange/libs/order/shared-model/src/lib/order-essentials';
import { useMyOrdersIndicators, AggregationOperation, OrderbookSide } from '@exchange/libs/orderbook/service/src';
import type { Orderbook } from '@exchange/libs/orderbook/service/src';
import { settingsService } from '@exchange/libs/settings/service/src';
import { logger } from '@exchange/libs/utils/simple-logger/src';

import OrderbookSeparator from './components/OrderbookSeparator.vue';
import { ORDERBOOK_RESIZE_DEBOUNCE_MS } from './constants.config';
import { LevelColumns, getColumns, getColumnTextAlignment } from './fast/OrderbookPricelevel';
import FastOrderbookSide from './fast/OrderbookPricelevels.vue';

const props = defineProps({
  aggregationExecute: { type: Function as PropType<(operation: AggregationOperation, marketPrecision?: number | undefined) => number | undefined>, required: true },
  aggregationLevel: { type: [Number, String], required: true },
  aggregationRaisePossible: { type: Boolean, required: true },
  aggregationReducePossible: { type: Boolean, required: true },
  amountPrecision: { type: Number, required: true },
  doScrollAtMiddle: { type: Function as PropType<(additionalOffset?: number) => void>, required: false },
  hasAnimations: { type: Boolean, required: true },
  lastPrice: { type: Number, required: true },
  marketId: { type: String, required: true },
  marketPrecision: { type: Number, required: true },
  mini: { type: Boolean, default: false },
  msPerFrame: { type: Number, required: true },
  orderbook: { type: Object as PropType<Orderbook>, required: true },
  orderbookAddedTime: { type: Number, default: 0 },
  orderbookResubscribe: { type: Function as PropType<() => Promise<void>>, required: true },
  totalPrecision: { type: Number, default: 0 },
  useFastOrderbook: { type: Boolean, default: true },
});

const { orderbook, marketId } = toRefs(props);

const asks = computed(() => props.orderbook.asks);
const bids = computed(() => props.orderbook.bids);
const spread = computed(() => props.orderbook.spread);
const asksMaxAmount = computed(() => Math.max(...asks.value.map((a) => a.amount)));
const bidsMaxTotal = computed(() => Math.max(...bids.value.map((b) => b.total)));
const spreadInBasePoints = computed(() => settingsService.settings.userSettings.orderbookSettings.spreadInBasePoints ?? false);

const columns = computed(() => getColumns(props.mini));
const orderType = computed(() => orderformService.data.type);
const root = ref<HTMLDivElement>();
const width = ref<number>(0);
let resizeObserver: ResizeObserver;
let onResize: (rect: DOMRectReadOnly) => void;

const { myOrderIndicators } = useMyOrdersIndicators({ orderbook, marketId });

const startObserving = () => {
  if (root.value && resizeObserver) {
    resizeObserver.observe(root.value);
  }
};

const recenter = () => {
  const additionalOffset = 0;

  props.doScrollAtMiddle?.(additionalOffset);
};

defineExpose({ recenter });

watch(root, startObserving);
watch(marketId, () => {
  recenter();
});

onMounted(() => {
  recenter();

  onResize = debounce(ORDERBOOK_RESIZE_DEBOUNCE_MS, (size: DOMRectReadOnly) => {
    width.value = size.width;
  });

  resizeObserver = new ResizeObserver((resizeEvents: Array<ResizeObserverEntry>) => {
    // eslint-disable-next-line no-restricted-syntax
    for (const event of resizeEvents) {
      if (onResize) {
        onResize(event.contentRect);
      }
    }
  });

  startObserving();
});

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
  }
});

const itemClicked = (item: { key: LevelColumns; amount: string; price: string; total: string }) => {
  if (orderType.value === OrderType.MARKET) {
    if (item.key === LevelColumns.amount) {
      orderformService.setAmount(item.amount);
    }
  } else {
    switch (item.key) {
      case LevelColumns.amount:
        orderformService.setAmount(item.amount);
        break;
      case LevelColumns.price:
        orderformService.setStopPrice(item.price);
        orderformService.setLimitPrice(item.price);
        break;
      case LevelColumns.total:
        orderformService.setStopPrice(item.price);
        orderformService.setLimitPrice(item.price);
        orderformService.setTotal(item.total);
        break;
      default:
        logger.warn('no type specified');
        break;
    }
  }
};
</script>

<template>
  <div
    ref="root"
    class="orderbook"
  >
    <div class="orderbook__header">
      <div
        v-for="header in columns"
        :key="header.lKey"
        class="orderbook__header-value"
        :class="`orderbook__header-value--align-${getColumnTextAlignment({ column: header, mini })}`"
      >
        {{ $t(header.lKey) }}
      </div>
    </div>
    <fast-orderbook-side
      :key="`ask-${orderbookAddedTime}-${orderbook.currentAggregationLevel}-${mini}`"
      class="orderbook__pricelevels asks"
      :side="OrderbookSide.ASK"
      :columns="columns"
      :mini="mini"
      :pricelevels="asks"
      :market-id="marketId"
      :amount-precision="amountPrecision"
      :market-precision="marketPrecision"
      :total-precision="totalPrecision"
      :max-amount="asksMaxAmount"
      :my-order-indicators="myOrderIndicators"
      :width="width"
      :ms-per-frame="msPerFrame"
      @item-clicked="(e) => itemClicked(e)"
    />
    <orderbook-separator
      class="orderbook__separator"
      :current-spread="spread"
      :spread-in-bps="spreadInBasePoints"
      :last-matched-price="lastPrice"
      :market-precision="marketPrecision"
      :reduce-possible="aggregationReducePossible"
      :raise-possible="aggregationRaisePossible"
      :level="aggregationLevel"
      :mini="mini"
      @aggregate="aggregationExecute"
    />
    <fast-orderbook-side
      :key="`bid-${orderbookAddedTime}-${orderbook.currentAggregationLevel}-${mini}`"
      class="orderbook__pricelevels bids"
      :side="OrderbookSide.BID"
      :columns="columns"
      :mini="mini"
      :pricelevels="bids"
      :market-id="marketId"
      :amount-precision="amountPrecision"
      :market-precision="marketPrecision"
      :total-precision="totalPrecision"
      :max-amount="bidsMaxTotal"
      :my-order-indicators="myOrderIndicators"
      :width="width"
      :ms-per-frame="msPerFrame"
      @item-clicked="(e) => itemClicked(e)"
    />
  </div>
</template>

<style lang="scss">
.orderbook {
  display: flex;
  flex-direction: column;
  height: 100%;
  user-select: none;

  &__header {
    position: sticky;
    z-index: 1;
    top: 0;
    left: 0;
    display: flex;
    flex: 0 0 auto;
    flex-direction: row;
    padding: 1px 10px;
    background-color: rgb(var(--v-theme-elevation-1));
    color: rgb(var(--v-theme-text-tertiary));
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-regular);

    .xs & {
      padding: 1px var(--padding-x-xs);
    }
  }

  &__header-value {
    height: 20px;
    flex: 1;
    text-align: right;

    &--align-left {
      text-align: left;
    }
  }

  &__separator {
    position: sticky;
    z-index: 2;
    top: 22px; /* orderbook__header height */
    bottom: 0;
  }

  &__pricelevels {
    z-index: 0;
    width: 100%;
    flex: 0 0 (62.5px * 20);
  }
}
</style>
